import React, { useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@roger/ui-components';
import { Field, DateField, DropdownField } from '../FormFields';
import { CheckCircle, Loader2, Check, AlertCircle } from 'lucide-react';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import { useIntake } from '../../hooks/useIntake';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import MarkCompletedButton from './MarkCompletedButton';
import { useIntakeFormSync } from '../../hooks/useIntakeFormSync';
import { useIntakeFieldSave } from '../../hooks/useIntakeFieldSave';
import { AUTHORIZATION_STATUS_OPTIONS } from '../patient/authorizationConstants';

interface PriorAuthStepLegacyProps {
  selectedIntake: IntakeRecord | null;
  agencyInsurancesLoading?: boolean;
  onStatusChange?: () => void | Promise<void>;
}

/**
 * Pre-Auth-Manager Prior Auth form, kept for intakes that do not use RogerEMR.
 * Free-text only: it never writes visit counts, an attachment, or an
 * Authorization record, none of which Kinnser consumes. RogerEMR intakes get
 * `PriorAuthStep` instead — see `usesRogerEmrForIntake`.
 */
const PriorAuthStepLegacy: React.FC<PriorAuthStepLegacyProps> = ({
  selectedIntake,
  agencyInsurancesLoading = false,
  onStatusChange,
}) => {
  const { updateIntakeRecord } = useIntake();
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);

  // No form validations on this tab; valid for Push to EMR gate (don't require "mark complete").
  useEffect(() => {
    setIntakeTabValid(IntakeTabValidationKey.PriorAuth, true);
  }, [setIntakeTabValid]);

  // Consolidated form state — synced from selectedIntake with keepDirtyValues.
  const [formData, setFormData] = useIntakeFormSync({
    selectedIntake,
    extract: (intake: IntakeRecord) => ({
      authorization_number: intake.authorization_number || '',
      authorization_request_date:
        typeof intake.authorization_request_date === 'string'
          ? intake.authorization_request_date
          : intake.authorization_request_date?.toISOString() || null,
      authorization_status: intake.authorization_status || 'NOT_STARTED',
      authorization_notes: intake.authorization_notes || '',
      authorized_disciplines_frequency:
        intake.authorized_disciplines_frequency || '',
    }),
    keepDirtyValues: true,
  });

  // Save utilities
  const { saveStatus, saveField } = useIntakeFieldSave({
    intakeId: selectedIntake?.id,
    updateIntakeRecord,
  });

  const updateField = useCallback(
    (fieldName: keyof typeof formData, value: string | null) => {
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    },
    [setFormData]
  );

  // Compare-and-save for a field. Accepts an optional explicit `value` for
  // fields (e.g. date, dropdown) that provide the value at call-time rather
  // than reading from formData.
  const handleBlur = useCallback(
    (fieldName: keyof typeof formData, value?: string | null) => {
      const currentValue = value !== undefined ? value : formData[fieldName];
      const originalValue = selectedIntake?.[fieldName] ?? '';
      const normalizedOriginal =
        fieldName === 'authorization_status'
          ? originalValue || 'NOT_STARTED'
          : originalValue || '';
      if (currentValue !== normalizedOriginal) {
        void saveField(fieldName, currentValue || null);
      }
    },
    [formData, selectedIntake, saveField]
  );

  if (!selectedIntake) {
    return null;
  }

  // The intake's own payer, even one the pickers no longer list.
  const getAgencyInsurance = () =>
    selectedIntake.agency_insurance_id ? selectedIntake.agency_insurance : null;

  // Helper function to check if prior auth is required for this intake
  const isPriorAuthRequired = () => {
    const agencyInsurance = getAgencyInsurance();
    return agencyInsurance?.visit_authorization_required ?? false;
  };

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-700'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='flex items-center gap-2 text-xs text-green-600'>
                <Check className='h-3 w-3' />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className='flex items-center gap-2 text-xs text-red-600'>
                <AlertCircle className='h-3 w-3' />
                <span>Save failed</span>
              </div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='prior_auth'
              isComplete={
                selectedIntake.tab_statuses?.prior_auth === 'complete' ||
                selectedIntake.tab_statuses?.prior_auth ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        {agencyInsurancesLoading ? (
          <div className='flex items-center justify-center h-full'>
            <div className='text-center'>
              <Loader2 className='h-8 w-8 animate-spin mx-auto mb-3' />
              <p className='text-sm font-medium text-gray-900 mb-1'>
                Loading insurance information...
              </p>
            </div>
          </div>
        ) : (
          <div className='space-y-6'>
            {/* Insurance Information Display */}
            <div className='border px-4 py-3 bg-gray-50'>
              <div className='font-semibold mb-2'>Insurance Information</div>
              <div className='text-sm'>
                {(() => {
                  const agencyInsuranceId = selectedIntake.agency_insurance_id;
                  if (!agencyInsuranceId) {
                    return (
                      <div className='text-orange-700'>
                        No agency insurance selected.
                      </div>
                    );
                  }

                  const agencyInsurance = getAgencyInsurance();

                  if (!agencyInsurance) {
                    return (
                      <div className='text-orange-700'>
                        Agency insurance not found
                      </div>
                    );
                  }

                  return (
                    <div className='space-y-1'>
                      <div>
                        <span className='font-medium'>Insurance:</span>{' '}
                        {agencyInsurance.name}
                      </div>
                      <div>
                        <span className='font-medium'>Payer ID:</span>{' '}
                        {agencyInsurance.edi_payer_id}
                      </div>
                      <div>
                        <span className='font-medium'>Clearinghouse:</span>{' '}
                        {agencyInsurance.edi_clearinghouse}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {getAgencyInsurance() ? (
              isPriorAuthRequired() ? (
                <div className='space-y-4'>
                  <div className='bg-orange-50 border border-orange-200 rounded-lg p-4'>
                    <div className='flex items-center mb-2'>
                      <h3 className='font-semibold text-orange-900'>
                        Prior Authorization Required
                      </h3>
                    </div>
                    <p className='text-sm text-orange-800'>
                      The selected insurance requires prior authorization.
                      Please call the insurance provider and enter the
                      authorization details below.
                    </p>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <Field
                      label='Authorization Number'
                      htmlFor='authorization_number'
                      value={formData.authorization_number}
                      onChange={v => updateField('authorization_number', v)}
                      onBlur={() => handleBlur('authorization_number')}
                    />
                    <DateField
                      label='Authorization Request Date'
                      htmlFor='authorization_request_date'
                      value={formData.authorization_request_date}
                      onChange={v =>
                        updateField('authorization_request_date', v ?? null)
                      }
                      onBlur={v => {
                        const nextValue = v ?? null;
                        updateField('authorization_request_date', nextValue);
                        handleBlur('authorization_request_date', nextValue);
                      }}
                    />
                  </div>

                  <DropdownField
                    label='Authorization Status'
                    htmlFor='authorization_status'
                    value={formData.authorization_status}
                    onChange={v => {
                      updateField('authorization_status', v);
                      handleBlur('authorization_status', v);
                    }}
                    options={AUTHORIZATION_STATUS_OPTIONS}
                    variant='formSelect'
                  />

                  <Field
                    label='Authorized Disciplines / Frequency'
                    htmlFor='authorized_disciplines_frequency'
                    value={formData.authorized_disciplines_frequency}
                    onChange={v =>
                      updateField('authorized_disciplines_frequency', v)
                    }
                    onBlur={() =>
                      handleBlur('authorized_disciplines_frequency')
                    }
                    inputProps={{
                      placeholder: 'e.g., PT 2x/wk, OT 1x/wk, SN 3x/wk',
                    }}
                  />

                  <Field
                    label='Authorization Notes'
                    htmlFor='authorization_notes'
                    value={formData.authorization_notes}
                    onChange={v => updateField('authorization_notes', v)}
                    onBlur={() => handleBlur('authorization_notes')}
                    multiline
                  />
                </div>
              ) : (
                <div className='space-y-4'>
                  <div className='bg-green-50 border border-green-200 rounded-lg p-6'>
                    <div className='flex items-center justify-center'>
                      <CheckCircle className='h-8 w-8 text-green-600 mr-3' />
                      <div>
                        <h3 className='font-semibold text-green-900'>
                          No Prior Authorization Required
                        </h3>
                        <p className='text-sm text-green-800'>
                          The selected insurance does not require prior
                          authorization.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Field
                    label='Authorization Notes'
                    htmlFor='authorization_notes'
                    value={formData.authorization_notes}
                    onChange={v => updateField('authorization_notes', v)}
                    onBlur={() => handleBlur('authorization_notes')}
                    multiline
                  />
                </div>
              )
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PriorAuthStepLegacy;
